/**
 * Persona doors — registry and remembered choice.
 *
 * Design: documents/WEBSITE_REBUILD_PERSONA_DOORS_AND_CONTENT_MATRIX.md (§4, §6).
 *
 * The visitor picks the door that matches their situation on first visit; the
 * site reorders itself around that choice (hero, header CTA, plan emphasis).
 * Doors never gate content — every page is reachable from every door.
 *
 * Storage contract (localStorage key `cs.persona`):
 *
 *     { "id": "developers",  "chosenAt": "2026-10-15T10:12:00.000Z" }   a door
 *     { "id": null,          "chosenAt": "2026-10-15T10:12:00.000Z" }   "Just browsing"
 *     (absent)                                                           never asked
 *
 * The explicit skip is remembered so the first-visit selector shows once and
 * only once. Every storage access is wrapped: with storage blocked the choice
 * still works for the current page load (module-level ref), it is simply not
 * remembered.
 *
 * Copy for the doors lives in src/locales/english.json under `persona.doors.*`
 * (labels) and `doors.*` (landing-page content); this module holds structure
 * only — ids, routes, CTA routes and plan emphasis — so the unit tests can
 * run it under plain Node without Vue.
 */
import { ref, computed } from 'vue'

export const PERSONA_STORAGE_KEY = 'cs.persona'

/**
 * The five doors, in selector order. `id` is stable and is the URL segment
 * under /for/. `plans` lists the tier_keys (from GET /api/cluster/plans) the
 * door emphasises, leading plan first; an empty list means "no price cards"
 * (door E shows a licence block only).
 */
export const DOORS = Object.freeze([
  Object.freeze({
    id: 'owners',
    slug: '/for/owners',
    labelKey: 'persona.doors.owners.label',
    shortKey: 'persona.doors.owners.short',
    nextKey: 'persona.doors.owners.next',
    cta: Object.freeze({ labelKey: 'persona.doors.owners.cta', to: '/marketplace', icon: 'fas fa-store' }),
    plans: Object.freeze(['starter', 'team']),
    technical: false
  }),
  Object.freeze({
    id: 'agencies',
    slug: '/for/agencies',
    labelKey: 'persona.doors.agencies.label',
    shortKey: 'persona.doors.agencies.short',
    nextKey: 'persona.doors.agencies.next',
    cta: Object.freeze({ labelKey: 'persona.doors.agencies.cta', to: '/signup', icon: 'fas fa-rocket' }),
    plans: Object.freeze(['team', 'professional']),
    technical: false
  }),
  Object.freeze({
    id: 'developers',
    slug: '/for/developers',
    labelKey: 'persona.doors.developers.label',
    shortKey: 'persona.doors.developers.short',
    nextKey: 'persona.doors.developers.next',
    cta: Object.freeze({ labelKey: 'persona.doors.developers.cta', to: '/docs', external: true, icon: 'fas fa-book' }),
    plans: Object.freeze(['starter']),
    technical: true
  }),
  Object.freeze({
    id: 'engineering-leads',
    slug: '/for/engineering-leads',
    labelKey: 'persona.doors.engineering-leads.label',
    shortKey: 'persona.doors.engineering-leads.short',
    nextKey: 'persona.doors.engineering-leads.next',
    cta: Object.freeze({ labelKey: 'persona.doors.engineering-leads.cta', to: '/pricing', icon: 'fas fa-scale-balanced' }),
    plans: Object.freeze(['professional', 'business', 'team']),
    technical: true
  }),
  Object.freeze({
    id: 'self-managed',
    slug: '/for/self-managed',
    labelKey: 'persona.doors.self-managed.label',
    shortKey: 'persona.doors.self-managed.short',
    nextKey: 'persona.doors.self-managed.next',
    cta: Object.freeze({ labelKey: 'persona.doors.self-managed.cta', to: '/contact?topic=self-managed', icon: 'fas fa-comments' }),
    plans: Object.freeze([]),
    technical: true
  })
])

export const DOOR_IDS = Object.freeze(DOORS.map((d) => d.id))

/** The "Just browsing" pseudo-door: id null, lands on the neutral homepage. */
export const BROWSING = Object.freeze({
  id: null,
  slug: '/',
  labelKey: 'persona.browsing.label',
  shortKey: 'persona.browsing.short',
  nextKey: 'persona.browsing.next'
})

export function getDoor(id) {
  if (!id) return null
  return DOORS.find((d) => d.id === id) || null
}

export function isDoorId(id) {
  return typeof id === 'string' && DOOR_IDS.includes(id)
}

/**
 * Route resolution for /for/:door. Returns the door for a known id and null
 * for anything else (the router then redirects to /404).
 */
export function resolveDoorRoute(param) {
  return isDoorId(param) ? getDoor(param) : null
}

// ── Storage ─────────────────────────────────────────────────────────────────

function storage(customStorage) {
  if (customStorage) return customStorage
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch (_) {
    return null
  }
}

/**
 * Read the stored choice. Returns `{ id, chosenAt }` for a door or the skip,
 * or null when nothing valid is stored (never asked, storage blocked, or a
 * stale/unknown door id from an older build).
 */
export function readPersona(customStorage) {
  try {
    const s = storage(customStorage)
    if (!s) return null
    const raw = s.getItem(PERSONA_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !('id' in parsed)) return null
    if (parsed.id !== null && !isDoorId(parsed.id)) return null
    return { id: parsed.id, chosenAt: typeof parsed.chosenAt === 'string' ? parsed.chosenAt : null }
  } catch (_) {
    return null
  }
}

/** Persist a door id (or null for the explicit skip). Returns the record. */
export function writePersona(id, customStorage, now = new Date()) {
  const record = { id: isDoorId(id) ? id : null, chosenAt: now.toISOString() }
  try {
    const s = storage(customStorage)
    if (s) s.setItem(PERSONA_STORAGE_KEY, JSON.stringify(record))
  } catch (_) {
    // Storage blocked (private mode, disabled cookies) — the choice still
    // applies to this page load through the reactive ref; it is not remembered.
  }
  return record
}

/** Forget the choice entirely so the first-visit selector shows again. */
export function erasePersona(customStorage) {
  try {
    const s = storage(customStorage)
    if (s) s.removeItem(PERSONA_STORAGE_KEY)
  } catch (_) {
    // ignore — see writePersona
  }
}

// ── Reactive state (shared across components) ───────────────────────────────

const personaRef = ref(undefined) // undefined = not yet read from storage

function ensureLoaded() {
  if (personaRef.value === undefined) {
    personaRef.value = readPersona()
  }
  return personaRef.value
}

/**
 * Composable. `persona` is the stored record (null when never asked),
 * `door` is the door object for a chosen door (null for skip / never asked),
 * `asked` is true once the visitor has either chosen or skipped.
 */
export function usePersona() {
  ensureLoaded()
  const persona = computed(() => personaRef.value || null)
  const door = computed(() => getDoor(personaRef.value && personaRef.value.id))
  const asked = computed(() => Boolean(personaRef.value))

  function setPersona(id) {
    personaRef.value = writePersona(id)
    return personaRef.value
  }

  function clearPersona() {
    erasePersona()
    personaRef.value = null
  }

  return { persona, door, asked, setPersona, clearPersona }
}

/** Test seam: reset the module-level ref (not used by app code). */
export function _resetPersonaState() {
  personaRef.value = undefined
}
